import React, { useState, useCallback, useEffect } from "react";
import Modal from '../../components/Modal';
import Dustbin from "../../components/DustbinMatch/DustbinMatch";
import Box from "../../components/BoxMatch/BoxMatch";
import update from "immutability-helper";
import styles from "./Match.module.css";
import { getMetrics, createMetrics, updateMetrics } from "../../service/service";
import LessonInstruction from "../../containers/instruction/LessonInstruction";
import { useAppGlobalConfig } from "../../providers/AppGlobalConfigContext";
import {getAudioUrl, getBrowser, getOS} from "../../lib/utils";
import useAudioPlayback from "../../hooks/useAudioPlayback";

const Match = props => {
  const { bookName, totalIndexes, lessonId, blockData, blockIndex, contentIndex, step, audioUrl } = props;
  const [isModalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [btnModalText, setBtnModalText] = useState("Tentar novamente");
  const [modalErrorMsg, setModalErrorMsg] = useState("Erro ao registrar seu progresso. Clique no botão abaixo para tentar novamente");
  const [initTime, setInitTime] = useState(Date.now())
  const [metrics, setMetrics] = useState(null)
  const [droppedBoxNames, setDroppedBoxNames] = useState([]);
  const [data, setData] = useState(blockData.content.filter(e => !e.instruction));
  const {isEmbedMode} = useAppGlobalConfig();
  let tenant = isEmbedMode ? 'cupheadless' : 'cupfranchise'
  const userInfo = JSON.parse(localStorage.getItem("user-info"));

  const { state, setAudioSrc} = useAudioPlayback(audioUrl, "audio");
  
  useEffect(() => {
    setData(blockData.content.filter(e => !e.instruction));
    getMetricsData();
    setDroppedBoxNames([]);
  }, [blockIndex]);

  const getData = useCallback(blockData.content.filter(e => !e.instruction).map(function (arr) {
    arr.lastDroppedItem = null;
    return arr;
  }), [blockIndex]);

  const closeModal = () => {
    setModalOpen(false);
    step('forward', true);
  };

  const buttonModalAction = async () => {
    setIsLoading(true);
    setBtnModalText('Enviando...');
    try {
      await registerProgress()
      setModalOpen(false);
      step('forward', true)
    } catch (error) {
      setModalErrorMsg("O erro ainda continua.. Tente novamente ou clique no botão de fechar para avançar para a próxima lição")
    }
    setIsLoading(false);
    setBtnModalText('Tentar novamente');
  };

  const [dustbins, setDustbins] = useState(getData);
  const [boxes, setBoxes] = useState(getData);

  // If all boxes have been dropped, and not playing audio, advance to the next block
  useEffect(() => {
    if (droppedBoxNames.length === data.length && state !== "playing") {
      step('forward', true);
    }
  }, [droppedBoxNames, state]);
  
  useEffect(() => {
    const sendData = async () => {
      try {
        await registerProgress()
      } catch (error) {
        setModalOpen(true);
      }
    }
    if (droppedBoxNames.length === data.length) {
      sendData()
    }
  }, [droppedBoxNames]);

  const registerProgress = async () => {
    const params = {
      "userId": userInfo.id,
      "blockId": blockData.block_id,
      "template": "match",
      "bookName": bookName,
      "lessonId": lessonId,
      "scores": [{
        "isRight": true,
        "accuracy": 100,
        "elapsedTime": Date.now() - initTime,
        "dateTime": new Date(initTime).toISOString(),
        "totalAttempts": 1
      }],
      "sectionId": blockData.sectionId,
      "section": blockData.section,
      "index": blockData.num,
      "totalIndexes": totalIndexes
    }
   
    if (metrics) {
      await updateMetrics(params, tenant)
    } else {
      await createMetrics(params, tenant)
    }
  }

  const getMetricsData = useCallback(async () => {
    try {
      const resp = await getMetrics(userInfo.id, blockData.block_id, tenant)
      if (resp.data) {
        setMetrics(resp.data)
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setMetrics(null)
      } else {
        console.error("Erro ao buscar métricas:", error);
      }
    }
  }, [blockIndex]);

  const isEquivalent = (a, b) => {
    let aProps = Object.getOwnPropertyNames(a);
    let bProps = Object.getOwnPropertyNames(b);

    // Então, é feita a verificação se os dois objetos possuem o mesmo
    // número de propriedades. Muito bom! E tira muito trabalho caso não
    // tenham.
    if (aProps.length !== bProps.length) {
      return false;
    }

    // Caso tenham o mesmo número, é realizada uma iteração por todas as
    // propriedades do objeto. Porém, como ressaltei antes, não busca por
    // objetos dentro de objetos, podendo retornar uma inconsistência.
    for (let i = 0; i < aProps.length; i++) {
      let propName = aProps[i];

      if (a[propName] !== b[propName]) {
        return false;
      }
    }

    return true;
  }

  if (!isEquivalent(boxes, getData)) {
    setDustbins(getData);
    setBoxes(getData);
  }

 
  const isDropped = (boxName) => {
    return droppedBoxNames.indexOf(boxName) > -1;
  }

  const playAudio = (index) => {
    let audioName = getBrowser() === 'Safari' || getOS() === 'iOS' ? dustbins[index].sound_mp3 : dustbins[index].sound;
    const audioURL = getAudioUrl(audioName);
    setAudioSrc(audioURL);
  }

  const handleDrop = useCallback(
    (index, item) => {
      const { name } = item;

      playAudio(index);

      setDroppedBoxNames(
        update(droppedBoxNames, name ? { $push: [name] } : { $push: [] })
      );
      setDustbins(
        update(dustbins, {
          [index]: {
            lastDroppedItem: {
              $set: item
            }
          }
        })
      );
    },
    [droppedBoxNames, dustbins]
  );

  const getShuffledArr = arr => {
    const newArr = arr.slice()
    let i = 0;
    let cont = 0;
    while (newArr) {
      if (typeof newArr[i] == 'undefined') break;
      if (!droppedBoxNames.includes(newArr[i]['en'])) {
        const rand = Math.floor(Math.random() * (cont + 1));
        [newArr[i], newArr[rand]] = [newArr[rand], newArr[i]];
        newArr[rand]['index'] = i;
        cont++;
      }
      i++;
    }
    return newArr
  };

  const [boxesRamdom, setBoxRamdom] = useState([]);
  useEffect(function () {
    const ramdom = getShuffledArr(boxes);
    setBoxRamdom(ramdom);
  }, [boxes, dustbins]);

  const { type, instruction } = blockData.content[contentIndex];
  return (
    <div className="default-card default-card-full-width" style={{padding: "1rem"}}>
      {
        instruction ?
          <LessonInstruction
            instructionText={instruction}
            audioUrl={audioUrl}
          />
          :
            <div className="match-container">
              <div className={styles.match_instruction_container}>
                <h3>Match the pictures to the sentences.</h3>
              </div>
              <div className={styles.match_image_item_container}>
                <Modal isOpen={isModalOpen}
                       onClose={closeModal}
                       isLoading={isLoading}
                       onActionButtonClick={buttonModalAction}
                       message={modalErrorMsg}
                       actionButtonText={btnModalText}/>
                {dustbins.map(({ type, en, picture, lastDroppedItem }, index) => (
                    <div className={styles.match_image_item + ""} key={index}>
                      <Dustbin
                          accepts={en}
                          lastDroppedItem={lastDroppedItem}
                          getPicture={picture}
                          name={en}
                          onDrop={item => handleDrop(index, item)}
                          key={index}
                      />
                    </div>
                ))}
              </div>
              <div className={styles.match_texts_container}>
                {boxesRamdom.map(({ en, type, index }) => (
                    <div className={styles.match_text_item + " text-center"} key={index}>
                      <Box name={en} type={en} isDropped={isDropped(en)} key={index}/>
                    </div>
                ))}
              </div>
            </div>
      }
    </div>
  );
};
export default Match;
